// Platform detection for web/mobile environments
import { getWorkdKey } from '../storage/workdStorage';
import { clearAllStores, useAuthStore } from '../stores';
import { Capacitor, CapacitorHttp } from '../utils/capacitorShim';
import ConfigService from './ConfigService';
import { TokenManager } from './TokenManager';

/**
 * localStorage key holding the reason for a forced logout (PD-14144). Set during
 * a forced logout AFTER auth storage is cleared, so it survives the OAuth
 * re-auth round-trip; read+cleared by the app on next load via
 * {@link consumeLogoutReason}. Deliberately NOT `workd:`-prefixed so it is not
 * swept by auth-storage clears.
 */
const LOGOUT_REASON_STORAGE_KEY = 'sm2.logoutReason';

/**
 * Read and clear the forced-logout reason persisted by {@link AuthService.logout}.
 * Returns the reason (e.g. `'session_expired'`) once, or `null` if none was set.
 * The app calls this on startup to show a "your session expired" notice after a
 * timed/forced logout (PD-14144).
 */
export function consumeLogoutReason(): string | null {
	if (typeof window === 'undefined' || !window.localStorage) {
		return null;
	}
	try {
		const reason = window.localStorage.getItem(LOGOUT_REASON_STORAGE_KEY);
		if (reason != null) {
			window.localStorage.removeItem(LOGOUT_REASON_STORAGE_KEY);
		}
		return reason;
	} catch {
		return null;
	}
}

/**
 * Auth storage keys - single source of truth for all auth-related localStorage keys.
 * Use these constants instead of hardcoded strings to ensure consistency.
 */
export const AUTH_STORAGE_KEYS = {
	/** Main auth state containing tokens, user, and instance info */
	AUTH_STATE: 'auth_state',
	/** PKCE code verifier for OAuth flow - uses workd: prefix so it's cleared on logout */
	PKCE_VERIFIER: 'workd:oauth:pkce_verifier',
	/** OAuth state for CSRF protection - uses workd: prefix so it's cleared on logout */
	OAUTH_STATE: 'workd:oauth:state',
	/** URL to return to after OAuth completes */
	RETURN_URL: 'auth_return_url',
	/** Lock for coordinating token refresh across tabs */
	REFRESH_LOCK: 'token_refresh_lock',
	/** Delegation state (isDelegated, actualUser, delegatedAsUser) */
	DELEGATION_STATE: 'delegation_state',
	/** Original auth state stored before delegation (for restoration) */
	ORIGINAL_AUTH_STATE: 'original_auth_state',
	/** Stores RingCentral call method preference (ringcentral vs phone_app) */
	RINGCENTRAL_CALL_METHOD_PREFERENCE: 'ringcentral_call_method_preference',
	/** Stores RingCentral selected device ID */
	RINGCENTRAL_DEVICE_ID: 'ringcentral_device_id',
	/** Stores RingCentral forwarding number ID for the selected device */
	RINGCENTRAL_FORWARDING_NUMBER_ID: 'ringcentral_forwarding_number_id',
	/** Stores RingCentral dial method (callout vs ringout) */
	RINGCENTRAL_DIAL_METHOD: 'ringcentral_dial_method',
	/** Stores RingCentral device name for display purposes */
	RINGCENTRAL_DEVICE_NAME: 'ringcentral_device_name',
	/** Stores user's phone number for RingOut calls */
	RINGCENTRAL_RINGOUT_PHONE_NUMBER: 'ringcentral_ringout_phone_number',
} as const;

/**
 * Authentication Service
 * Handles OAuth flow and token management for both web and mobile
 */
export interface User {
	id: number;
	instanceUserId?: number; // Explicit instance user ID
	username: string;
	email?: string;
	firstName?: string;
	lastName?: string;
	phoneNumber?: string;
	languagePreference?: string;
	roles?: Array<string>; // User's RBAC roles (e.g., ["Admin", "Salesperson"])
}

export interface Instance {
	id: number;
	alias: string;
	name: string;
	active: boolean;
}

export interface ProviderDiscoveryResult {
	type: 'internal' | 'external';
	authorizationUrl: string;
	provider: {
		type: string;
		name: string;
	};
	instance?: {
		id: number;
		alias: string;
		name: string;
	};
	sessionId: string;
}

export interface TokenResponse {
	access_token: string;
	token_type: string;
	expires_in: number;
	refresh_token?: string;
	scope?: string;
}

export interface AuthState {
	isAuthenticated: boolean;
	isLoading?: boolean;
	user: User | null;
	instance: Instance | null;
	accessToken: string | null;
	refreshToken: string | null;
	expiresAt: number | null;
	authMode: 'jwt' | 'apikey';
	apiKey: string | null;
}

export class AuthService {
	private static instance: AuthService;
	private authState: AuthState = {
		isAuthenticated: false,
		user: null,
		instance: null,
		accessToken: null,
		refreshToken: null,
		expiresAt: null,
		authMode: 'jwt',
		apiKey: null,
	};

	private listeners: Array<(state: AuthState) => void> = [];
	private readonly authClearedCallbacks: Array<() => void> = [];
	private readonly baseUrl: string;
	private readonly clientId: string;
	private readonly tokenManager: TokenManager;

	/** Cross-tab logout channel — see setupLogoutChannel(). */
	private static readonly LOGOUT_CHANNEL_NAME = 'workd:auth:logout';
	private static readonly LOGOUT_MESSAGE = 'logout';
	private logoutChannel: BroadcastChannel | null = null;

	private constructor(baseUrl: string, clientId: string) {
		this.baseUrl = baseUrl;
		this.clientId = clientId;
		this.tokenManager = new TokenManager();
		this.loadStoredAuth();
		this.initializeApiServiceIntegration();
		this.setupStorageListener();
		this.setupLogoutChannel();
	}

	/**
	 * Cross-tab logout channel (PD-13055).
	 *
	 * The `storage` event already syncs a logout to other tabs, but only once
	 * the storage write happens — and `logout()` awaits the server-side token
	 * revocation BEFORE clearing local storage. For the whole duration of that
	 * request the other tabs still believe they are signed in and will happily
	 * issue authenticated calls with a token that is being revoked.
	 *
	 * Broadcasting the intent as the very first thing logout does collapses that
	 * window to a single task tick. BroadcastChannel is not available
	 * everywhere (older Safari, non-browser contexts), so this is strictly an
	 * accelerator — the existing storage-event path remains the fallback and is
	 * unchanged.
	 */
	private setupLogoutChannel(): void {
		if (
			typeof window === 'undefined' ||
			typeof BroadcastChannel === 'undefined'
		) {
			return;
		}

		try {
			this.logoutChannel = new BroadcastChannel(
				AuthService.LOGOUT_CHANNEL_NAME,
			);
			this.logoutChannel.onmessage = (event: MessageEvent): void => {
				if (event.data === AuthService.LOGOUT_MESSAGE) {
					this.applyRemoteLogout();
				}
			};
		} catch {
			// A blocked or unavailable channel is non-fatal — fall back to the
			// storage event.
			this.logoutChannel = null;
		}
	}

	/** Notify other tabs that a logout has started. Best-effort. */
	private broadcastLogout(): void {
		try {
			this.logoutChannel?.postMessage(AuthService.LOGOUT_MESSAGE);
		} catch {
			// Channel closed or unavailable — the storage event still covers it.
		}
	}

	/**
	 * Clear local auth state in response to another tab logging out. Does NOT
	 * re-broadcast (which would loop) and does NOT call the server (the tab that
	 * initiated the logout owns the revocation).
	 */
	private applyRemoteLogout(): void {
		if (!this.authState.isAuthenticated) {
			return;
		}

		this.updateAuthState({
			isAuthenticated: false,
			user: null,
			instance: null,
			accessToken: null,
			refreshToken: null,
			expiresAt: null,
			authMode: 'jwt',
			apiKey: null,
		});
	}

	public static getInstance(baseUrl?: string, clientId?: string): AuthService {
		if (!AuthService.instance) {
			if (!baseUrl || !clientId) {
				throw new Error(
					'AuthService must be initialized with baseUrl and clientId',
				);
			}
			AuthService.instance = new AuthService(baseUrl, clientId);
		}
		return AuthService.instance;
	}

	/**
	 * Add auth state listener
	 */
	public addListener(listener: (state: AuthState) => void): () => void {
		this.listeners.push(listener);

		return () => {
			const index = this.listeners.indexOf(listener);

			if (index > -1) {
				this.listeners.splice(index, 1);
			}
		};
	}

	/**
	 * Register a callback to be invoked when auth is fully cleared (logout, session reset, etc.)
	 * This allows other services (like DelegationService) to clean up their state in a DRY manner.
	 * @param callback Function to call when auth is cleared
	 * @returns Unsubscribe function
	 */
	public onAuthCleared(callback: () => void): () => void {
		this.authClearedCallbacks.push(callback);

		return () => {
			const index = this.authClearedCallbacks.indexOf(callback);

			if (index > -1) {
				this.authClearedCallbacks.splice(index, 1);
			}
		};
	}

	/**
	 * Get current auth state
	 */
	public getAuthState(): AuthState {
		return { ...this.authState };
	}

	/**
	 * Discover OAuth provider for user
	 */
	public async discoverProvider(
		username: string,
	): Promise<ProviderDiscoveryResult> {
		try {
			// Determine the redirect URI based on platform
			let redirectUri: string;

			if (typeof window !== 'undefined' && window.location) {
				// Use current origin for web
				redirectUri = window.location.origin;
			} else {
				// Use mobile deep link for React Native
				redirectUri = 'workd://oauth/callback';
			}

			const response = await fetch(`${this.baseUrl}/auth/discover`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					username,
					redirectUri,
				}),
			});

			if (!response.ok) {
				const error = await response
					.json()
					.catch(() => ({ message: 'Provider discovery failed' }));
				throw new Error(error.message || 'Provider discovery failed');
			}

			return await response.json();
		} catch (error) {
			// // Error: Provider discovery failed
			throw error;
		}
	}

	/**
	 * Generate a cryptographically random code verifier for PKCE
	 * @returns A random string of 128 URL-safe characters
	 */
	private generateCodeVerifier(): string {
		const array = new Uint8Array(96); // 96 bytes = 128 base64url chars
		if (typeof window !== 'undefined' && window.crypto) {
			window.crypto.getRandomValues(array);
		} else {
			// Fallback for Node.js/React Native
			for (let i = 0; i < array.length; i++) {
				array[i] = Math.floor(Math.random() * 256);
			}
		}

		// Convert to base64url
		return this.base64UrlEncode(array);
	}

	/**
	 * Generate code challenge from verifier using SHA256
	 * @param verifier The code verifier
	 * @returns The base64url encoded SHA256 hash
	 */
	private async generateCodeChallenge(verifier: string): Promise<string> {
		if (
			typeof window !== 'undefined' &&
			window.crypto &&
			window.crypto.subtle
		) {
			// Web browser with Web Crypto API
			const encoder = new TextEncoder();
			const data = encoder.encode(verifier);
			const hash = await window.crypto.subtle.digest('SHA-256', data);
			return this.base64UrlEncode(new Uint8Array(hash));
		} else {
			// Fallback: use plain method (less secure but works everywhere)
			// In production, you'd want to use a crypto library for React Native
			// Warning: Web Crypto API not available, using plain PKCE method
			return verifier;
		}
	}

	/**
	 * Base64url encode (no padding, URL-safe characters)
	 */
	private base64UrlEncode(buffer: Uint8Array): string {
		const base64 = btoa(String.fromCharCode(...buffer));
		return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
	}

	/**
	 * Generate and store PKCE parameters
	 * @returns Object containing code_verifier and code_challenge
	 */
	public async generatePKCEParams(): Promise<{
		verifier: string;
		challenge: string;
		method: string;
	}> {
		const verifier = this.generateCodeVerifier();
		const challenge = await this.generateCodeChallenge(verifier);
		const method = challenge === verifier ? 'plain' : 'S256';

		// Store verifier for later use in token exchange
		this.storePkceVerifier(verifier);

		return { verifier, challenge, method };
	}

	/**
	 * Clear OAuth flow state from localStorage.
	 * Called when starting a fresh OAuth flow to ensure no stale state.
	 * Uses dedicated localStorage keys (not Zustand persist) to avoid race conditions.
	 */
	private clearOAuthFlowState(): void {
		if (typeof localStorage !== 'undefined') {
			localStorage.removeItem(AUTH_STORAGE_KEYS.OAUTH_STATE);
			localStorage.removeItem(AUTH_STORAGE_KEYS.PKCE_VERIFIER);
		}
		// Also clear from Zustand for consistency
		useAuthStore.getState().setOAuthState(null);
		useAuthStore.getState().setPkceVerifier(null);
	}

	/**
	 * Store OAuth state in localStorage.
	 * Uses a dedicated key separate from Zustand persist to avoid race conditions.
	 */
	private storeOAuthState(state: string): void {
		if (typeof localStorage !== 'undefined') {
			localStorage.setItem(AUTH_STORAGE_KEYS.OAUTH_STATE, state);
		}
		// Also store in Zustand for compatibility
		useAuthStore.getState().setOAuthState(state);
	}

	/**
	 * Get OAuth state from localStorage.
	 */
	private getOAuthState(): string | null {
		if (typeof localStorage !== 'undefined') {
			return localStorage.getItem(AUTH_STORAGE_KEYS.OAUTH_STATE);
		}
		return null;
	}

	/**
	 * Store PKCE verifier in localStorage.
	 */
	private storePkceVerifier(verifier: string): void {
		if (typeof localStorage !== 'undefined') {
			localStorage.setItem(AUTH_STORAGE_KEYS.PKCE_VERIFIER, verifier);
		}
		// Also store in Zustand for compatibility
		useAuthStore.getState().setPkceVerifier(verifier);
	}

	/**
	 * Get PKCE verifier from localStorage.
	 */
	private getPkceVerifier(): string | null {
		if (typeof localStorage !== 'undefined') {
			return localStorage.getItem(AUTH_STORAGE_KEYS.PKCE_VERIFIER);
		}
		return null;
	}

	/**
	 * Start a fresh OAuth flow.
	 * This is the proper entry point when the user needs to authenticate.
	 * It clears any stale OAuth state and sets up fresh values.
	 *
	 * @param redirectUri - The URI to redirect back to after authentication
	 * @returns The authorization URL to redirect the user to
	 */
	public async startFreshOAuthFlow(redirectUri: string): Promise<string> {
		// Clear any existing OAuth state - we're starting fresh
		this.clearOAuthFlowState();

		// Generate fresh OAuth state for CSRF protection
		const state = this.generateOAuthState();
		this.storeOAuthState(state);

		// Build the authorization URL with PKCE
		const params = new URLSearchParams({
			response_type: 'code',
			client_id: this.clientId,
			redirect_uri: redirectUri,
			scope: 'read write',
			state,
		});

		// Generate fresh PKCE parameters
		const verifier = this.generateCodeVerifier();
		const challenge = await this.generateCodeChallenge(verifier);
		const method = challenge === verifier ? 'plain' : 'S256';

		// Store the verifier for token exchange
		this.storePkceVerifier(verifier);

		params.append('code_challenge', challenge);
		params.append('code_challenge_method', method);

		return `${this.baseUrl}/auth/oauth/authorize?${params.toString()}`;
	}

	/**
	 * Generate a random OAuth state string for CSRF protection.
	 */
	private generateOAuthState(): string {
		const array = new Uint8Array(32);
		crypto.getRandomValues(array);
		return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join(
			'',
		);
	}

	/**
	 * OAuth Authorization Code Flow (for web) with PKCE
	 * @deprecated Use startFreshOAuthFlow instead for proper OAuth state management
	 */
	public async initiateOAuthFlow(
		redirectUri: string,
		state?: string,
	): Promise<string> {
		const params = new URLSearchParams({
			response_type: 'code',
			client_id: this.clientId,
			redirect_uri: redirectUri,
			scope: 'read write',
		});

		if (state) {
			params.append('state', state);
			// Store the state if provided
			this.storeOAuthState(state);
		}

		// Generate and add PKCE parameters - REQUIRED
		try {
			const pkce = await this.generatePKCEParams();

			// Verify PKCE parameters were generated successfully
			if (!pkce.challenge || !pkce.method) {
				throw new Error('Failed to generate PKCE parameters');
			}

			params.append('code_challenge', pkce.challenge);
			params.append('code_challenge_method', pkce.method);
		} catch (error) {
			// // Error: Failed to generate PKCE parameters
			throw new Error(
				'PKCE is required for OAuth flow but failed to generate parameters',
			);
		}

		return `${this.baseUrl}/auth/oauth/authorize?${params.toString()}`;
	}

	/**
	 * Exchange authorization code for tokens with PKCE
	 * @param code - The authorization code from the OAuth callback
	 * @param redirectUri - The redirect URI used in the authorization request
	 * @param state - Optional state parameter from the OAuth callback for CSRF validation
	 */
	public async exchangeCodeForTokens(
		code: string,
		redirectUri: string,
		state?: string,
	): Promise<TokenResponse> {
		// Validate state parameter if provided (CSRF protection)
		// Read from localStorage - this is the authoritative source for OAuth flow state
		if (state != null) {
			const storedState = this.getOAuthState();
			if (state !== storedState) {
				throw new Error('OAuth state validation failed. Please try again.');
			}
		}

		// Retrieve the stored code verifier from localStorage
		const codeVerifier = this.getPkceVerifier();

		// PKCE is REQUIRED for all OAuth flows per security requirements
		if (codeVerifier == null) {
			throw new Error(
				'code_verifier is required for authorization code exchange. PKCE is mandatory for all OAuth flows.',
			);
		}

		const tokenRequest: Record<string, string> = {
			grant_type: 'authorization_code',
			client_id: this.clientId,
			code,
			redirect_uri: redirectUri,
			code_verifier: codeVerifier,
		};

		const tokenUrl = `${this.baseUrl}/auth/oauth/token`;
		let tokens: TokenResponse;

		// Use native HTTP on mobile platforms to avoid WebView networking issues
		if (Capacitor.isNativePlatform()) {
			try {
				const result = await CapacitorHttp.post({
					url: tokenUrl,
					headers: { 'Content-Type': 'application/json' },
					data: tokenRequest,
				});

				if (result.status < 200 || result.status >= 300) {
					const error = result.data ?? { error: 'Token exchange failed' };
					throw new Error(
						error.error_description ?? error.error ?? 'Token exchange failed',
					);
				}

				tokens = result.data as TokenResponse;
			} catch (httpError: unknown) {
				const err = httpError as Error;
				console.error('[AuthService] CapacitorHttp token exchange error:', err);
				throw new Error(
					`Network error during token exchange: ${err?.message ?? 'Unknown error'}`,
				);
			}
		} else {
			// Use fetch for web platforms
			let response: Response;
			try {
				response = await fetch(tokenUrl, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify(tokenRequest),
				});
			} catch (fetchError: unknown) {
				const err = fetchError as Error;
				console.error('[AuthService] Fetch token exchange error:', err);
				throw new Error(
					`Network error during token exchange: ${err?.message ?? 'Unknown error'}`,
				);
			}

			if (!response.ok) {
				const error = await response
					.json()
					.catch(() => ({ error: 'Token exchange failed' }));
				throw new Error(
					error.error_description || error.error || 'Token exchange failed',
				);
			}

			tokens = await response.json();
		}

		// SUCCESS — the code is spent, so retire the whole OAuth flow state.
		//
		// This previously cleared only the Zustand copy of the verifier, which
		// looked like a one-time-use clear but was not one: getPkceVerifier()
		// and getOAuthState() both read from localStorage, and neither
		// localStorage key was touched. A completed flow therefore stayed
		// fully replayable.
		//
		// That is not theoretical. On native, the app re-processes its launch
		// URL (CapacitorApp.getLaunchUrl()) and the in-memory
		// processedOAuthCodes guard in App.tsx is reset by any remount — such
		// as the one that follows a successful sign-in. The stale callback then
		// passed the state check, presented the already-consumed code, and the
		// server correctly answered 401 "Invalid authorization code", which the
		// app surfaced as a dead-end "Authentication Error" moments after a
		// login that had actually succeeded. Observed on device: tokens issued
		// at 00:00:31, the same code re-presented and rejected at 00:00:54.
		//
		// Clearing both localStorage keys makes the replay fail the state check
		// harmlessly instead, which is what that check was there to do.
		this.clearOAuthFlowState();

		// Update internal state but DON'T set isAuthenticated yet
		this.authState = {
			isAuthenticated: false,
			user: null, // Will be fetched next
			instance: null, // Will be fetched next
			accessToken: tokens.access_token,
			refreshToken: tokens.refresh_token ?? null,
			expiresAt: Date.now() + tokens.expires_in * 1000,
			authMode: 'jwt',
			apiKey: null,
		};

		// Fetch user and instance info now that we have a valid token
		// IMPORTANT: We must do this BEFORE broadcasting isAuthenticated = true
		try {
			// Temporarily set to true so fetchTokenInfo doesn't throw
			this.authState.isAuthenticated = true;
			await this.fetchTokenInfo();
		} catch (error) {
			// fetchTokenInfo failed - reset to unauthenticated
			console.error(
				'[AuthService] Failed to fetch token info during exchange:',
				error,
			);
			this.authState.isAuthenticated = false;

			throw error;
		}

		// Store complete auth state to localStorage
		this.storeAuth();

		// Broadcast authenticated state with complete user/instance info
		this.listeners.forEach((listener) => {
			listener(this.authState);
		});

		return tokens;
	}

	/**
	 * Refresh access token (with multi-tab coordination)
	 * Called when we get a 401 response - always performs refresh
	 */
	public async refreshAccessToken(): Promise<boolean> {
		if (!this.authState.refreshToken) {
			return false;
		}

		try {
			// TokenManager coordinates refresh across tabs
			// Only one tab refreshes, others wait for result
			const newToken = await this.tokenManager.coordinateRefresh(() =>
				this.doRefreshToken(),
			);

			return newToken != null;
		} catch (error) {
			console.error('[AuthService] Token refresh error:', error);

			return false;
		}
	}

	/**
	 * Internal method to actually perform the token refresh
	 * Returns the new access token or null on failure
	 */
	private async doRefreshToken(): Promise<string | null> {
		try {
			const url = `${this.baseUrl}/auth/oauth/token`;
			const payload = {
				grant_type: 'refresh_token',
				client_id: this.clientId,
				refresh_token: this.authState.refreshToken,
			};

			let tokens: TokenResponse;

			// Native uses CapacitorHttp, matching exchangeCodeForTokens and
			// fetchTokenInfo. This method was the odd one out, and the
			// inconsistency was load-bearing: plain fetch from the WebView is
			// subject to CORS, so refresh failed with a bare "Load failed"
			// while the initial sign-in — which already used native HTTP —
			// succeeded. That produced an "Authentication Error" moments after
			// a login the server had accepted.
			//
			// It normally hides behind CapacitorHttp's global fetch patch, which
			// routes WebView fetches through native. That patch does not apply
			// when the app is served from a remote origin (CAP_ENV=development
			// points the shell at the Vite dev server), so the gap only shows up
			// there — but the fix belongs here regardless, because an auth path
			// should not depend on a global monkey-patch that a supported
			// configuration turns off.
			if (Capacitor.isNativePlatform()) {
				const result = await CapacitorHttp.post({
					url,
					headers: { 'Content-Type': 'application/json' },
					data: payload,
				});

				if (result.status < 200 || result.status >= 300) {
					console.error('[AuthService] Refresh error response:', result.status);
					// Refresh rejected (expired/invalidated session) — forced logout.
					void this.logout({ reason: 'session_expired' });

					return null;
				}

				tokens = result.data as TokenResponse;
			} else {
				const response = await fetch(url, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify(payload),
				});

				if (!response.ok) {
					const errorText = await response.text().catch(() => 'No error text');
					console.error('[AuthService] Refresh error response:', errorText);
					// Refresh rejected (expired/invalidated session) — forced logout.
					this.logout({ reason: 'session_expired' });

					return null;
				}

				tokens = await response.json();
			}

			this.updateAuthState({
				...this.authState,
				isAuthenticated: true,
				isLoading: false,
				accessToken: tokens.access_token,
				refreshToken: tokens.refresh_token || this.authState.refreshToken,
				expiresAt: Date.now() + tokens.expires_in * 1000,
			});

			this.storeAuth();

			return tokens.access_token;
		} catch (error) {
			console.error('[AuthService] Token refresh error:', error);
			this.logout();

			return null;
		}
	}

	/**
	 * Establish a session from a refresh token obtained OUTSIDE the normal
	 * PKCE flow — today that means biometric sign-in (PD-14659): the OS-gated
	 * keychain read returns the refresh token persisted at enrollment, and
	 * this exchanges it for a full session (refresh grant → token info →
	 * listener broadcast), mirroring exchangeCodeForTokens without the
	 * code/PKCE steps.
	 *
	 * Unlike doRefreshToken(), a rejected grant must NOT force the logout
	 * round-trip: this runs pre-auth (there is no session to tear down), and
	 * the caller needs to distinguish outcomes — 'rejected' (token revoked or
	 * expired → disable biometric sign-in and fall back to credentials) vs
	 * 'error' (network/server blip → keep the stored credential and let the
	 * user retry).
	 */
	public async loginWithRefreshToken(
		refreshToken: string,
		// PD-15748 — optionally sign in to a DIFFERENT instance the user has
		// access to (the biometric tenant chooser); the server verifies
		// membership. Omitted = the token's own instance, as always.
		instanceId?: number,
	): Promise<'success' | 'rejected' | 'error'> {
		const tokenUrl = `${this.baseUrl}/auth/oauth/token`;
		const tokenRequest = {
			grant_type: 'refresh_token',
			client_id: this.clientId,
			refresh_token: refreshToken,
			...(instanceId != null ? { instance_id: instanceId } : {}),
		};

		let tokens: TokenResponse;
		try {
			// Native uses CapacitorHttp for the same reason exchangeCodeForTokens
			// does (WebView networking issues); the refresh grant carries the
			// client_id in the body, so it has no Origin/Referer dependency.
			if (Capacitor.isNativePlatform()) {
				const result = await CapacitorHttp.post({
					url: tokenUrl,
					headers: { 'Content-Type': 'application/json' },
					data: tokenRequest,
				});

				if (result.status < 200 || result.status >= 300) {
					console.debug(
						'[AuthService] loginWithRefreshToken rejected:',
						result.status,
					);
					// 4xx = the token itself was refused; anything else is transient.
					return result.status >= 400 && result.status < 500
						? 'rejected'
						: 'error';
				}

				tokens = result.data as TokenResponse;
			} else {
				const response = await fetch(tokenUrl, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify(tokenRequest),
				});

				if (!response.ok) {
					console.debug(
						'[AuthService] loginWithRefreshToken rejected:',
						response.status,
					);
					return response.status >= 400 && response.status < 500
						? 'rejected'
						: 'error';
				}

				tokens = (await response.json()) as TokenResponse;
			}
		} catch (error) {
			console.error(
				'[AuthService] loginWithRefreshToken network error:',
				error,
			);
			return 'error';
		}

		// The server may rotate the refresh token on this exchange; fall back to
		// the presented token when the response omits it (or sends it empty).
		const rotatedRefreshToken =
			tokens.refresh_token != null && tokens.refresh_token !== ''
				? tokens.refresh_token
				: refreshToken;

		this.authState = {
			...this.authState,
			// fetchTokenInfo requires isAuthenticated; reset on its failure below.
			isAuthenticated: true,
			user: null,
			instance: null,
			accessToken: tokens.access_token,
			refreshToken: rotatedRefreshToken,
			expiresAt: Date.now() + tokens.expires_in * 1000,
			authMode: 'jwt',
			apiKey: null,
		};

		try {
			await this.fetchTokenInfo();
		} catch (error) {
			console.error(
				'[AuthService] loginWithRefreshToken token-info failed:',
				error,
			);
			this.authState.isAuthenticated = false;
			return 'error';
		}

		this.storeAuth();
		this.listeners.forEach((listener) => {
			listener(this.authState);
		});

		return 'success';
	}

	/**
	 * PD-15748 — list the instances the given refresh token's user can sign
	 * in to, WITHOUT spending the (single-use) token. Read-only server call;
	 * powers the biometric tenant chooser. Returns null on ANY failure so the
	 * caller falls back to the plain sign-in — a chooser is a nicety, a
	 * blocked login is not.
	 */
	public async listInstancesForRefreshToken(
		refreshToken: string,
	): Promise<Array<{ id: number; name: string; alias: string }> | null> {
		const url = `${this.baseUrl}/auth/oauth/instances`;
		const body = {
			client_id: this.clientId,
			refresh_token: refreshToken,
		};
		try {
			let payload: {
				success?: boolean;
				instances?: Array<{ id: number; name: string; alias: string }>;
			};
			if (Capacitor.isNativePlatform()) {
				const result = await CapacitorHttp.post({
					url,
					headers: { 'Content-Type': 'application/json' },
					data: body,
				});
				if (result.status < 200 || result.status >= 300) {
					return null;
				}
				payload = result.data as typeof payload;
			} else {
				const response = await fetch(url, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify(body),
				});
				if (!response.ok) {
					return null;
				}
				payload = (await response.json()) as typeof payload;
			}
			if (payload.success !== true || payload.instances == null) {
				return null;
			}
			return payload.instances;
		} catch (error) {
			console.debug(
				'[AuthService] listInstancesForRefreshToken failed:',
				error,
			);
			return null;
		}
	}

	/**
	 * Check if token needs refresh
	 */
	public needsTokenRefresh(): boolean {
		if (!this.authState.expiresAt) {
			return true;
		} // No expiry = needs refresh
		// Check if token is expired OR will expire within 5 minutes
		return (
			Date.now() >= this.authState.expiresAt ||
			this.authState.expiresAt - Date.now() < 5 * 60 * 1000
		);
	}

	/**
	 * Get valid access token (auto-refresh if needed)
	 */
	public async getAccessToken(): Promise<string | null> {
		if (!this.authState.isAuthenticated) {
			return null;
		}

		// Return API key if in API key mode
		if (this.authState.authMode === 'apikey') {
			return this.authState.apiKey;
		}

		const needsRefresh = this.needsTokenRefresh();

		if (needsRefresh) {
			const refreshed = await this.refreshAccessToken();
			if (!refreshed) {
				return null;
			}
		}

		return this.authState.accessToken;
	}

	/**
	 * Ensure ApiService has a valid token
	 * Call this before making API requests to ensure token is fresh
	 */
	public async ensureValidToken(): Promise<boolean> {
		const token = await this.getAccessToken();
		if (token) {
			return true;
		}
		return false;
	}

	/**
	 * Fetch user and instance information from the token
	 */
	public async fetchTokenInfo(): Promise<void> {
		if (!this.authState.isAuthenticated || !this.authState.accessToken) {
			throw new Error('Cannot fetch token info without valid authentication');
		}

		try {
			// Get the API base URL from ConfigService
			const apiUrl = ConfigService.getInstance().getApiBaseUrl();
			const tokenInfoUrl = `${apiUrl}/system/token`;

			let tokenInfo: Record<string, unknown>;

			// Use native HTTP on mobile platforms to avoid WebView networking issues
			if (Capacitor.isNativePlatform()) {
				const result = await CapacitorHttp.get({
					url: tokenInfoUrl,
					headers: {
						Authorization: `Bearer ${this.authState.accessToken}`,
						'Content-Type': 'application/json',
					},
				});

				if (result.status < 200 || result.status >= 300) {
					throw new Error(`Failed to fetch token info: ${result.status}`);
				}

				tokenInfo = result.data as Record<string, unknown>;
			} else {
				// Use regular fetch for web platforms
				const response = await fetch(tokenInfoUrl, {
					method: 'GET',
					headers: {
						Authorization: `Bearer ${this.authState.accessToken}`,
						'Content-Type': 'application/json',
					},
				});

				if (!response.ok) {
					throw new Error(`Failed to fetch token info: ${response.statusText}`);
				}

				tokenInfo = await response.json();
			}

			if (tokenInfo != null) {
				// Update auth state with the fetched information
				// Store both IDs separately for clarity
				const userInfo = {
					id: parseInt((tokenInfo.userId as string) ?? '0'), // DVP/Workd user ID
					instanceUserId: tokenInfo.instanceUserId
						? parseInt(tokenInfo.instanceUserId as string)
						: undefined, // Tenant instance user ID
					username: (tokenInfo.email as string)?.split('@')[0] ?? 'user',
					email: tokenInfo.email as string,
					firstName: (tokenInfo.firstName ?? tokenInfo.first_name) as string,
					lastName: (tokenInfo.lastName ?? tokenInfo.last_name) as string,
					phoneNumber: (tokenInfo.phoneNumber ??
						tokenInfo.phone_number) as string,
					languagePreference:
						((tokenInfo.languagePreference ??
							tokenInfo.language_preference) as string) ?? 'en',
					roles: (tokenInfo.roles as Array<string>) ?? [],
				};

				this.authState = {
					...this.authState,
					user: userInfo,
					instance: tokenInfo.tenant
						? {
								id: (tokenInfo.instanceId as number) ?? 0,
								alias: tokenInfo.tenant as string,
								name:
									((tokenInfo.instanceName ??
										tokenInfo.database ??
										tokenInfo.tenant) as string) ?? '',
								active: (tokenInfo.instanceActive as boolean) ?? true,
							}
						: null,
				};

				this.storeAuth();
				// Notify listeners that we have updated the auth state with user/instance info
				this.listeners.forEach((listener) => {
					listener(this.authState);
				});
			}
		} catch (error) {
			console.error('[AuthService] fetchTokenInfo error:', error);
			throw error;
		}
	}

	/**
	 * Initialize ApiService integration (no longer needed but kept for compatibility)
	 */
	public initializeApiServiceIntegration(): void {
		// ApiService now gets auth info directly from AuthService
		// This method is kept for backward compatibility but does nothing
	}

	/**
	 * Handle authentication failure (401 response)
	 * This is called by ApiService when a 401 is received
	 */
	public async handleAuthFailure(currentUrl?: string): Promise<void> {
		// Store the return URL for after re-authentication
		if (currentUrl && typeof window !== 'undefined') {
			this.storeReturnUrl(currentUrl);
		}

		// Forced logout — surface a "session expired" notice after re-auth.
		await this.logout({ reason: 'session_expired' });
	}

	/**
	 * Logout.
	 *
	 * @param options.reason When set (forced logout — expired session, failed
	 *   refresh, 401), the reason is persisted to localStorage so the app can
	 *   show a "your session expired" message after the OAuth re-auth round-trip
	 *   (PD-14144). Omitted for an explicit user-initiated logout (no message).
	 * @param options.preserveServerSession When true, skip the server-side
	 *   token revocation and clear local state only. Used by native biometric
	 *   sign-in (PD-14659): the refresh token persisted behind the OS
	 *   biometric gate must stay valid so "Sign in with Face ID" works after
	 *   an explicit sign-out. Every plain-storage copy of the session is
	 *   still cleared — post-logout the token exists ONLY in the
	 *   biometry-gated keychain entry.
	 */
	public async logout(options?: {
		reason?: string;
		preserveServerSession?: boolean;
	}): Promise<void> {
		// PD-13055: tell the other tabs FIRST, before the revocation round-trip.
		// Everything below this line can take hundreds of milliseconds, and until
		// it completes the other tabs would otherwise keep issuing authenticated
		// requests with a token that is in the process of being revoked.
		this.broadcastLogout();

		try {
			if (
				this.authState.accessToken &&
				options?.preserveServerSession !== true
			) {
				await fetch(`${this.baseUrl}/auth/logout`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${this.authState.accessToken}`,
					},
					body: JSON.stringify({
						token: this.authState.accessToken,
						refreshToken: this.authState.refreshToken,
					}),
				});
			}
		} catch (error) {
			// Logout request failed - continue with local cleanup
		}

		this.updateAuthState({
			isAuthenticated: false,
			user: null,
			instance: null,
			accessToken: null,
			refreshToken: null,
			expiresAt: null,
			authMode: 'jwt',
			apiKey: null,
		});

		// ApiService will get auth state from AuthService

		// Use clearAllAuthStorage() for explicit logout to clear all user preferences (security)
		this.clearAllAuthStorage();

		// Persist the forced-logout reason AFTER clearing storage so it survives
		// the OAuth re-auth round-trip and the app can surface it on next load
		// (PD-14144). Uses a non-`workd:` key so it is not swept by auth-storage
		// clears. Omitted for explicit user logout.
		if (
			options?.reason != null &&
			typeof window !== 'undefined' &&
			window.localStorage
		) {
			try {
				window.localStorage.setItem(LOGOUT_REASON_STORAGE_KEY, options.reason);
			} catch {
				// Ignore storage errors (private mode, quota) — message is best-effort.
			}
		}

		// Redirect to root if we're in a browser environment
		// The root route will trigger the OAuth flow for re-authentication
		if (typeof window !== 'undefined') {
			// Use replace instead of href to prevent going back to the logged out state
			window.location.replace('/');
		}
	}

	/**
	 * Update auth state and notify listeners
	 */
	private updateAuthState(newState: AuthState): void {
		this.authState = { ...newState };
		this.listeners.forEach((listener) => {
			listener(this.authState);
		});
	}

	/**
	 * Store auth state to Zustand store (persisted via workdStorage)
	 */
	private storeAuth(): void {
		try {
			// Sync internal state to Zustand store (which persists via workdStorage)
			useAuthStore.getState().setAuth({
				isAuthenticated: this.authState.isAuthenticated,
				user: this.authState.user,
				instance: this.authState.instance,
				accessToken: this.authState.accessToken,
				refreshToken: this.authState.refreshToken,
				expiresAt: this.authState.expiresAt,
				authMode: this.authState.authMode,
				apiKey: this.authState.apiKey,
			});
		} catch (error) {
			console.error('[AuthService] Failed to store auth state:', error);
		}
	}

	/**
	 * Load stored auth state from Zustand store
	 */
	private loadStoredAuth(): void {
		try {
			const storeState = useAuthStore.getState();

			// Check if we have stored auth data
			if (storeState.accessToken != null || storeState.apiKey != null) {
				// Build complete state from store
				const completeState: AuthState = {
					isAuthenticated: storeState.isAuthenticated,
					user: storeState.user,
					instance: storeState.instance,
					accessToken: storeState.accessToken,
					refreshToken: storeState.refreshToken,
					expiresAt: storeState.expiresAt,
					authMode: storeState.authMode,
					apiKey: storeState.apiKey,
				};

				// Validate based on auth mode
				if (
					completeState.authMode === 'apikey' &&
					completeState.apiKey != null
				) {
					// API key mode - no expiration check needed
					this.authState = completeState;
					// Mark auth as initialized - API key is ready to use
					useAuthStore.getState().setAuthInitialized(true);
				} else if (
					completeState.expiresAt != null &&
					completeState.expiresAt > Date.now()
				) {
					// JWT mode with valid token
					this.authState = completeState;

					// Mark auth as initialized - valid token is ready to use
					useAuthStore.getState().setAuthInitialized(true);

					// Notify listeners that auth state has been restored
					this.listeners.forEach((listener) => {
						listener(this.authState);
					});

					// If we don't have user or instance data, fetch it
					if (
						this.authState.isAuthenticated &&
						(this.authState.user == null || this.authState.instance == null)
					) {
						this.fetchTokenInfo().catch(() => {});
					}
				} else if (storeState.refreshToken != null) {
					// Token expired but we have a refresh token - try to refresh
					// Keep isAuthenticated as false until refresh succeeds to prevent
					// race conditions where API calls are made with expired tokens
					// Set isLoading to true so App.tsx waits for refresh to complete
					this.authState = {
						...completeState,
						isAuthenticated: false,
						isLoading: true,
					};

					// Sync to Zustand store IMMEDIATELY so ThemeProvider and other
					// consumers see the loading state before we start async refresh.
					// Without this, the store has stale isAuthenticated=true from persistence.
					useAuthStore.getState().setAuth({
						isAuthenticated: false,
						isLoading: true,
					});

					// Notify listeners that we're loading (so App.tsx can show loading state)
					this.listeners.forEach((listener) => {
						listener(this.authState);
					});

					this.refreshAccessToken().then((refreshed) => {
						if (!refreshed) {
							// Refresh failed, clear auth state
							this._clearStoredAuth();
							this.authState = {
								isAuthenticated: false,
								isLoading: false,
								user: null,
								instance: null,
								accessToken: null,
								refreshToken: null,
								expiresAt: null,
								authMode: 'jwt',
								apiKey: null,
							};
							// Notify listeners
							this.listeners.forEach((listener) => {
								listener(this.authState);
							});
						}
						// Mark auth as initialized after refresh completes (success or failure)
						// This allows consumers to proceed with API calls (or redirect to login)
						useAuthStore.getState().setAuthInitialized(true);
					});
				} else {
					// No valid auth data - mark as initialized so consumers can proceed
					this._clearStoredAuth();
					useAuthStore.getState().setAuthInitialized(true);
				}
			} else {
				// No stored auth data at all - mark as initialized so consumers can proceed
				useAuthStore.getState().setAuthInitialized(true);
			}
		} catch (error) {
			console.error('[AuthService] Failed to load auth state:', error);
			// Even on error, mark as initialized so the app doesn't hang
			useAuthStore.getState().setAuthInitialized(true);
		}
	}

	/**
	 * Store return URL for after re-authentication
	 */
	public storeReturnUrl(url: string): void {
		useAuthStore.getState().setReturnUrl(url);
	}

	/**
	 * Get and clear stored return URL
	 */
	public getAndClearReturnUrl(): string | null {
		const url = useAuthStore.getState().returnUrl;

		if (url != null) {
			useAuthStore.getState().setReturnUrl(null);

			return url;
		}

		return null;
	}

	/**
	 * Clear only auth tokens (for session expiry scenarios).
	 * Preserves user preferences - same user will re-authenticate.
	 */
	private _clearAuthTokensOnly(): void {
		try {
			// Clear auth tokens in the store
			useAuthStore.getState().setTokens(null, null, null);
			useAuthStore.getState().setAuthenticated(false);
			useAuthStore.getState().setRefreshLock(false);
		} catch {
			// Silently fail if store not available
		}
	}

	/**
	 * Clear stored auth state (internal use)
	 * Uses _clearAuthTokensOnly() to preserve user preferences during session expiry
	 */
	private _clearStoredAuth(): void {
		this._clearAuthTokensOnly();
	}

	/**
	 * Clear all auth-related storage.
	 * Call this when you need to fully reset auth state (e.g., on explicit logout).
	 * This clears all user preferences for security (different user may log in next).
	 * NOTE: PKCE_VERIFIER is NOT cleared here - it has its own cleanup in exchangeCodeForTokens()
	 * after successful exchange. Clearing it here would cause race conditions.
	 */
	public clearAllAuthStorage(): void {
		try {
			// Use centralized store cleanup - clears all Zustand stores,
			// workd: prefixed keys, and legacy storage keys
			clearAllStores();

			// Notify all registered callbacks that auth has been cleared
			// This allows services like DelegationService to clear their in-memory state
			this.authClearedCallbacks.forEach((callback) => {
				try {
					callback();
				} catch (error) {
					console.error('[AuthService] Error in auth cleared callback:', error);
				}
			});
		} catch (error) {
			console.error('[AuthService] Error clearing auth storage:', error);
		}
	}

	/**
	 * Set auth mode (jwt or apikey)
	 */
	public setAuthMode(mode: 'jwt' | 'apikey'): void {
		if (this.authState.authMode !== mode) {
			this.updateAuthState({
				...this.authState,
				authMode: mode,
			});

			// ApiService will get auth info from AuthService when needed
		}
	}

	/**
	 * Get current auth mode
	 */
	public getAuthMode(): 'jwt' | 'apikey' {
		return this.authState.authMode;
	}

	/**
	 * Set API key for authentication
	 */
	public setApiKey(apiKey: string): void {
		this.updateAuthState({
			...this.authState,
			isAuthenticated: true,
			authMode: 'apikey',
			apiKey,
			// Clear JWT-related fields when switching to API key
			accessToken: null,
			refreshToken: null,
			expiresAt: null,
		});
		// ApiService will get the API key from AuthService when needed

		this.storeAuth();
	}

	/**
	 * Get API key
	 */
	public getApiKey(): string | null {
		return this.authState.apiKey;
	}

	/**
	 * Clear API key
	 */
	public clearApiKey(): void {
		this.updateAuthState({
			...this.authState,
			isAuthenticated: false,
			apiKey: null,
		});
		// ApiService will get auth state from AuthService

		this.storeAuth();
	}

	/**
	 * Get authentication header value based on current mode
	 */
	public getAuthHeader(): { name: string; value: string } | null {
		if (!this.authState.isAuthenticated) {
			return null;
		}

		if (this.authState.authMode === 'apikey' && this.authState.apiKey) {
			return { name: 'X-API-Key', value: this.authState.apiKey };
		} else if (
			this.authState.authMode === 'jwt' &&
			this.authState.accessToken
		) {
			return {
				name: 'Authorization',
				value: `Bearer ${this.authState.accessToken}`,
			};
		}

		return null;
	}

	/**
	 * Get authentication type (for identifying auth method)
	 */
	public getAuthType(): 'Bearer' | 'ApiKey' | null {
		if (!this.authState.isAuthenticated) {
			return null;
		}

		return this.authState.authMode === 'apikey' ? 'ApiKey' : 'Bearer';
	}

	/**
	 * Setup storage listener to sync auth state across tabs
	 */
	private setupStorageListener(): void {
		if (typeof window === 'undefined') {
			return;
		}

		const AUTH_STATE_KEY = getWorkdKey('auth', 'state');

		window.addEventListener('storage', (event) => {
			// Listen for auth state changes from other tabs
			if (event.key === AUTH_STATE_KEY && event.newValue != null) {
				try {
					// Zustand persist stores as { state: {...}, version: N }
					const parsed = JSON.parse(event.newValue);
					const newState = parsed.state ?? parsed;

					// Check if the token actually changed
					if (newState.accessToken !== this.authState.accessToken) {
						// Update our state without broadcasting (avoid circular updates)
						this.authState = {
							isAuthenticated: newState.isAuthenticated ?? false,
							user: newState.user ?? null,
							instance: newState.instance ?? null,
							accessToken: newState.accessToken ?? null,
							refreshToken: newState.refreshToken ?? null,
							expiresAt: newState.expiresAt ?? null,
							authMode: newState.authMode ?? 'jwt',
							apiKey: newState.apiKey ?? null,
						};

						// Notify local listeners
						this.listeners.forEach((listener) => {
							listener(this.authState);
						});
					}
				} catch (error) {
					console.error('[AuthService] Failed to parse storage event:', error);
				}
			}

			// Handle logout from other tabs
			if (event.key === AUTH_STATE_KEY && event.newValue == null) {
				this.authState = {
					isAuthenticated: false,
					user: null,
					instance: null,
					accessToken: null,
					refreshToken: null,
					expiresAt: null,
					authMode: 'jwt',
					apiKey: null,
				};
				this.listeners.forEach((listener) => {
					listener(this.authState);
				});
			}
		});
	}

	/**
	 * Cleanup method (for completeness, though this is a singleton)
	 */
	public cleanup(): void {
		this.tokenManager.cleanup();
		this.listeners = [];
	}
}
